import type { FunctionComponent } from 'react'
import type { AdminShiftTileProps } from './AdminShiftTile'
import styles from './DayShiftsGraph.module.sass'

export type DayShiftsGraphProps = {
	positions: AdminShiftTileProps['day']['shiftGroups']
}

export const DayShiftsGraph: FunctionComponent<DayShiftsGraphProps> = ({ positions }) => {
	const filteredPositions = positions
		?.map((position) => ({
			type: position.type,
			shifts: position.shifts.filter(
				(shift) => Boolean(shift.shiftsProfile?.staff?.id) && shift.queueItem
			),
		}))
		.filter((position) => position.shifts.length > 0)

	const minStartMinutes = Math.min(
		...(filteredPositions
			?.map(
				(position) =>
					position.shifts
						.map((shift) => {
							const hoursAndMinutes = shift.queueItem?.start?.split(':')
							if (hoursAndMinutes) {
								return parseInt(hoursAndMinutes[0]) * 60 + parseInt(hoursAndMinutes[1])
							}
							return 0
						})
						.flat() ?? 0
			)
			?.flat() ?? [])
	)
	const minStartHours = Math.floor(minStartMinutes / 60)

	const maxEndMinutes = Math.max(
		...(filteredPositions
			?.map(
				(position) =>
					position.shifts
						.map((shift) => {
							const hoursAndMinutes = shift.queueItem?.end?.split(':')
							if (hoursAndMinutes) {
								return parseInt(hoursAndMinutes[0]) * 60 + parseInt(hoursAndMinutes[1])
							}
							return 24 * 60
						})
						.flat() ?? 24 * 60
			)
			?.flat() ?? [])
	)
	const maxEndHours = Math.ceil(maxEndMinutes / 60)

	const hours = Array.from(
		{ length: maxEndHours - minStartHours },
		(_, index) => index + Math.floor(minStartHours)
	)

	const labelFrequency = Math.round(hours.length / 6)

	return (
		<div className={styles.wrapper}>
			{filteredPositions?.map((position, positionIndex) => (
				<div className={styles.position} key={`position-${positionIndex}`}>
					<div className={styles.positionName}>{position.type?.name}</div>
					<div
						className={styles.items}
						style={{
							'--DayShiftsGraph-totalHours': hours.length,
							'--DayShiftsGraph-startHours': minStartHours,
						}}>
						{hours.map((hour, index) => {
							return (
								<div
									key={hour}
									className={styles.hour}
									style={{ '--DayShiftsGraph-hour-counter': hour }}>
									{index % labelFrequency === 0 && (
										<span className={styles.hourLabel}>{`${hour}:00`}</span>
									)}
								</div>
							)
						})}
						{position.shifts
							.filter((shift) => Boolean(shift.shiftsProfile?.staff?.id))
							.map((shift, shiftIndex) => (
								<DayShiftsGraphItem
									key={`shift-${positionIndex}-${shiftIndex}`}
									shift={shift}
									minStartMinutes={minStartMinutes - (minStartMinutes % 60)} // rounded down to the hour
									maxEndMinutes={maxEndMinutes + (maxEndMinutes % 60)} // rounded up to the hour
								/>
							))}
					</div>
				</div>
			))}
		</div>
	)
}

const DayShiftsGraphItem: FunctionComponent<{
	shift: NonNullable<DayShiftsGraphProps['positions']>[number]['shifts'][number]
	minStartMinutes: number
	maxEndMinutes: number
}> = ({ shift, minStartMinutes, maxEndMinutes }) => {
	const startTime = (shift.queueItem?.start ?? '00:00').split(':')
	const endTime = (shift.queueItem?.end ?? '24:00').split(':')

	const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1])
	const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1])

	const startOffset = (startMinutes - minStartMinutes) / (maxEndMinutes - minStartMinutes)
	const endOffset = (endMinutes - minStartMinutes) / (maxEndMinutes - minStartMinutes)

	const label = `${shift.shiftsProfile?.staff?.user?.name ?? 'unknown'} (${
		shift.queueItem?.start ?? '??'
	} - ${shift.queueItem?.end ?? '??'})`

	return (
		<div className={styles.item}>
			<div
				className={styles.bar}
				style={{
					'--DayShiftsGraphItem-start': `${(startOffset * 100).toString()}%`,
					'--DayShiftsGraphItem-width': `${((endOffset - startOffset) * 100).toString()}%`,
				}}
				title={label}>
				{label}
			</div>
		</div>
	)
}
